import { useState, useEffect } from "react";
import {
  onAuthStateChanged,
  signInWithPopup,
  signOut as firebaseSignOut,
  type User,
} from "firebase/auth";
import { auth, googleProvider } from "@/lib/firebase";

const ALLOWED_EMAILS = ["evelina@elitemind.co.uk", "steven@elitemind.co.uk"];

interface AuthState {
  user: User | null;
  loading: boolean;
  error: string | null;
}

export function useAuth() {
  const [state, setState] = useState<AuthState>({
    user: null,
    loading: true,
    error: null,
  });

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(
      auth,
      (user) => {
        if (user && !ALLOWED_EMAILS.includes(user.email ?? "")) {
          firebaseSignOut(auth);
          setState({
            user: null,
            loading: false,
            error: `Access denied for ${user.email}. Only authorised Elite Mind team members can sign in.`,
          });
          return;
        }
        setState({ user, loading: false, error: null });
      },
      (error) => {
        setState({ user: null, loading: false, error: error.message });
      },
    );
    return unsubscribe;
  }, []);

  const signInWithGoogle = async () => {
    try {
      setState((s) => ({ ...s, error: null }));
      const result = await signInWithPopup(auth, googleProvider);
      const email = result.user.email ?? "";
      if (!ALLOWED_EMAILS.includes(email)) {
        await firebaseSignOut(auth);
        setState({
          user: null,
          loading: false,
          error: `Access denied for ${email}. Only authorised Elite Mind team members can sign in.`,
        });
      }
    } catch (err) {
      setState((s) => ({
        ...s,
        error: err instanceof Error ? err.message : "Sign-in failed",
      }));
    }
  };

  const signOut = async () => {
    await firebaseSignOut(auth);
  };

  return {
    user: state.user,
    loading: state.loading,
    error: state.error,
    signInWithGoogle,
    signOut,
  };
}
